import React from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Article as ArticleIcon,
  LocalHospital as DischargeIcon,
  AssignmentInd as ReferralIcon,
} from '@mui/icons-material';
import type { DocumentSummary, DocumentStatus } from '../../types/documents';

interface DocumentsListProps {
  documents: DocumentSummary[];
  selectedDocumentId?: string;
  onDocumentSelect: (document: DocumentSummary) => void;
}

const getDocumentIcon = (templateName?: string) => {
  if (!templateName) return <DescriptionIcon />;

  const name = templateName.toLowerCase();
  if (name.includes('discharge')) return <DischargeIcon color="primary" />;
  if (name.includes('referral')) return <ReferralIcon color="secondary" />;
  if (name.includes('case') || name.includes('summary')) return <ArticleIcon />;
  return <DescriptionIcon />;
};

const getStatusChip = (status: DocumentStatus) => {
  switch (status) {
    case 'DRAFT':
      return <Chip label="Draft" size="small" color="warning" variant="outlined" />;
    case 'FINAL':
      return <Chip label="Final" size="small" color="success" variant="outlined" />;
    case 'ARCHIVED':
      return <Chip label="Archived" size="small" color="default" variant="outlined" />;
  }
};

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  selectedDocumentId,
  onDocumentSelect,
}) => {
  if (documents.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <DescriptionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No documents yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Ask the copilot to write discharge papers, case summaries, or other clinical documents.
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ p: 0, width: '100%' }}>
      {documents.map((doc) => (
        <ListItem key={doc.documentId} sx={{ p: 0 }}>
          <ListItemButton
            selected={selectedDocumentId === doc.documentId}
            onClick={() => onDocumentSelect(doc)}
            sx={{ py: 2, px: 2 }}
          >
            <ListItemIcon>
              {getDocumentIcon(doc.templateName)}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1" component="span" noWrap>
                    {doc.title}
                  </Typography>
                  {getStatusChip(doc.status)}
                </Box>
              }
              secondary={
                <Box>
                  {doc.templateName && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {doc.templateName}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      v{doc.version}
                      {doc.versionCount > 1 && ` (${doc.versionCount} versions)`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(doc.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
};

export default DocumentsList;
